import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  DocumentTextIcon,
  CreditCardIcon,
  UserGroupIcon,
  CogIcon,
  ChartBarIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../UI/Logo';

const Sidebar = () => {
  // const { user, isAdmin } = useAuth(); // Ancienne version conservée pour référence
  const { user } = useAuth();
  const location = useLocation();

  const mutualiteNavigation = [
    { name: 'Tableau de bord', href: '/', icon: HomeIcon },
    { name: 'Produits', href: '/offers', icon: CreditCardIcon },
    { name: 'Mes bénéficiaires', href: '/beneficiaries', icon: UserGroupIcon },
    { name: 'Mes souscriptions', href: '/subscriptions', icon: DocumentTextIcon },
    { name: 'Mes polices', href: '/policies', icon: ShieldCheckIcon },
    { name: 'Mon profil', href: '/profile', icon: CogIcon },
  ];

  const adminNavigation = [
    { name: 'Tableau de bord', href: '/', icon: ChartBarIcon },
    { name: 'Mutualistes', href: '/admin/mutualistes', icon: UsersIcon },
    { name: 'Souscriptions', href: '/admin/subscriptions', icon: DocumentTextIcon },
    { name: 'Polices', href: '/admin/policies', icon: ShieldCheckIcon },
    { name: 'Demandes', href: '/admin/requests', icon: ClipboardDocumentListIcon },
    { name: 'Paiements', href: '/payments/unified', icon: BanknotesIcon },
    { name: 'Paramétrage', href: '/admin/parametrage', icon: WrenchScrewdriverIcon },
    { name: 'Mon profil', href: '/profile', icon: CogIcon },
  ];

  // Menu pour AGENT et SUPERVISEUR (comme admin mais sans Paramétrage + Produits)
  const agentSuperviseurNavigation = [
    { name: 'Tableau de bord', href: '/', icon: ChartBarIcon },
    { name: 'Produits', href: '/offers', icon: CreditCardIcon },
    { name: 'Mutualistes', href: '/admin/mutualistes', icon: UsersIcon },
    { name: 'Souscriptions', href: '/admin/subscriptions', icon: DocumentTextIcon },
    { name: 'Polices', href: '/admin/policies', icon: ShieldCheckIcon },
    { name: 'Demandes', href: '/admin/requests', icon: ClipboardDocumentListIcon },
    { name: 'Paiements', href: '/payments/unified', icon: BanknotesIcon },
    { name: 'Mon profil', href: '/profile', icon: CogIcon },
  ];

  // Sélection du menu selon le rôle
  const getNavigation = () => {
    if (user?.role === 'ADMIN') return adminNavigation;
    if (user?.role === 'AGENT' || user?.role === 'SUPERVISEUR') return agentSuperviseurNavigation;
    return mutualiteNavigation;
  };

  const navigation = getNavigation();

  return (
    <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
      <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
        {/* Logo */}
        <div className="flex items-center flex-shrink-0 px-4">
          <Logo size="medium" />
        </div>

        {/* Navigation */}
        <nav className="mt-8 flex-1 px-2 space-y-1">
          {navigation.map((item) => {
            // Vérifier si c'est le menu Paramétrage et si on est sur une sous-page
            const isParametrageActive = item.name === 'Paramétrage' && 
              (location.pathname === item.href || 
               location.pathname.startsWith('/admin/users') ||
               location.pathname.startsWith('/admin/status') ||
               location.pathname.startsWith('/admin/genres') ||
               location.pathname.startsWith('/admin/fonctions') ||
               location.pathname.startsWith('/admin/entreprises') ||
               location.pathname.startsWith('/admin/lien-parente') ||
               location.pathname.startsWith('/admin/situation-matrimoniale'));
            
            // Vérifier si c'est le menu Paiements et si on est sur une sous-page
            const isPaiementsActive = item.name === 'Paiements' && 
              (location.pathname.startsWith('/admin/payments') ||
               location.pathname.startsWith('/payments/') ||
               location.pathname === '/contribution-calls' ||
               location.pathname === '/enterprise-payment-config');
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    isActive || isParametrageActive || isPaiementsActive
                      ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className={`mr-3 flex-shrink-0 h-6 w-6 ${
                        isActive || isParametrageActive || isPaiementsActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    {item.name}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Informations utilisateur */}
        <div className="flex-shrink-0 px-4 py-4 border-t border-gray-200">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.mutualiste 
                    ? `${user.mutualiste.nom?.[0] || ''}${user.mutualiste.prenoms?.[0] || ''}`
                    : user?.role === 'ADMIN'
                      ? 'AD'
                      : user?.role === 'AGENT'
                        ? 'AG'
                      : user?.role === 'SUPERVISEUR'
                        ? 'SU'
                        : user?.email?.[0]?.toUpperCase() || 'U'
                }
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">
                {user?.mutualiste 
                    ? `${user.mutualiste.nom} ${user.mutualiste.prenoms}`
                    : user?.role === 'ADMIN'
                      ? 'Administrateur'
                      : user?.role === 'AGENT'
                        ? 'Agent'
                      : user?.role === 'SUPERVISEUR'
                        ? 'Superviseur'
                        : user?.email
                }
              </p>
              <p className="text-xs text-gray-500">
                {user?.role === 'ADMIN' 
                  ? 'Administrateur'
                  : user?.role === 'AGENT'
                    ? 'Agent'
                    : user?.role === 'SUPERVISEUR'
                      ? 'Superviseur'
                      : user?.mutualiste 
                        ? `${user.mutualiste.matricule}` 
                        : 'Utilisateur'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
